import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { Download, HelpCircle, Loader2, Undo2, Upload } from 'lucide-react';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  exportOasisBatch,
  listOasisManagerVisits,
  markVisitsExported,
  precheckOasisBatch,
  unmarkVisitsExported,
} from '../services/api';
import type { OasisManagerVisitItem } from '../services/api/types.gen';
import type { OASISManagerFilters } from '../store/slices/oasisManagerSlice';
import { formatDateToMMDDYYYY, parseISOToLocalDate } from '../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToastContext';
import { getVisitTypeDisplayName } from '../hooks/useVisitTypeOptions';
import {
  getResponseDetailFromBlobOrJson,
  getSurfaceableError,
  getUserFacingApiMessage,
} from '../utils/apiErrorDetail';
import { getHttpStatus } from '../utils/httpErrorUtils';
import { downloadBlobAsFile } from '../utils/downloadFile';
import { OpenOasisReportWithQueryState } from './OpenOasisReport';
import { ImportedPatientBadge } from '../components/patient/ImportedPatientBadge';
import { TestPatientBadge } from '../components/patient/TestPatientBadge';
import { ReportLink } from '../components/reporting/ReportLink';
import {
  ContactSupportLink,
  SupportMessage,
} from '../components/support/ContactSupportLink';

const STATUS_BY_TAB: Record<
  Exclude<OASISManagerFilters['activeTab'], 'incomplete'>,
  string
> = {
  ready_to_export: 'completed',
  exported: 'exported',
};

// Zero-padded CMS correction numbers. 00 = new submission; 01, 02, … are
// resubmissions of an already-accepted OASIS. The backend accepts 00–99; the
// dropdown is capped at 10, which covers realistic resubmission counts.
const CORRECTION_NUM_OPTIONS = Array.from({ length: 11 }, (_, i) =>
  String(i).padStart(2, '0')
);
const DEFAULT_CORRECTION_NUM = '00';

// CMS transaction type follows from the correction number: a new submission
// (00) is trans_type 1; any correction is a modification (trans_type 2).
function transTypeForCorrectionNum(correctionNum: string): '1' | '2' {
  return correctionNum === DEFAULT_CORRECTION_NUM ? '1' : '2';
}

function daysUntilDeadline(visitDate: string): number {
  const d = parseISOToLocalDate(visitDate);
  if (!d) return 0;
  const deadlineUtc = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate() + 30);
  const now = new Date();
  const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((deadlineUtc - todayUtc) / (1000 * 60 * 60 * 24));
}

const OASIS_MANAGER_TAB_VALUES = [
  'incomplete',
  'ready_to_export',
  'exported',
] as const;

/**
 * The page's one blocking-error surface: a dismissible destructive Alert.
 *
 * Every export failure renders through this rather than a toast — a toast is
 * string-only, so it can't carry the "Contact Support" escape hatch, and it
 * disappears before the user can act on it.
 */
const DismissibleExportError: React.FC<{
  title: string;
  onDismiss: () => void;
  children: React.ReactNode;
}> = ({ title, onDismiss, children }) => (
  <Alert variant='destructive' className='mb-4 flex items-start gap-3'>
    <div className='flex-1'>
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className='text-muted-foreground'>
        {children}
      </AlertDescription>
    </div>
    <Button size='sm' variant='ghost' onClick={onDismiss}>
      Dismiss
    </Button>
  </Alert>
);

/**
 * A failed export, ready to render. `body` overrides the rendered message when
 * it needs markup (a link to the fix); `detail` overrides what support is told
 * when the user-facing copy is too generic to act on. Both default to
 * `message`, which is the common case.
 */
type ExportError = {
  title: string;
  message: string;
  body?: React.ReactNode;
  detail?: string;
  /** The assessments this failure is about, captured when it happened. The
   * copy tells the user to uncheck the failing rows, so reading the live
   * selection later would report whatever survived that edit — or nothing. */
  visitIds: string[];
};

const EXPORT_FAILED_TITLE = 'Export failed';

/**
 * The visit ids CMS (iQIES) named in a VUT rejection. Shape is documented on
 * the backend surfaceable-error contract; anything unexpected returns null so
 * the caller falls back to what it actually sent.
 */
function rejectedVisitIds(data: Record<string, unknown>): string[] | null {
  const assessments = (data as { assessments?: unknown }).assessments;
  if (!Array.isArray(assessments)) return null;
  const ids = assessments
    .map(a => (a as { visit_id?: unknown })?.visit_id)
    .filter((id): id is string => typeof id === 'string');
  return ids.length > 0 ? ids : null;
}

const OASISManager: React.FC = () => {
  const { oasisManagerFilters, setOASISManagerFilters } = useStore(
    useShallow(state => ({
      oasisManagerFilters: state.oasisManagerFilters,
      setOASISManagerFilters: state.setOASISManagerFilters,
    }))
  );

  const [visits, setVisits] = useState<OasisManagerVisitItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Per-visit correction number chosen for the next export (transient — the
  // user sets it based on what CMS has already accepted; not persisted).
  const [correctionNums, setCorrectionNums] = useState<Record<string, string>>(
    {}
  );
  const [isExporting, setIsExporting] = useState(false);
  const [activeTab, setActiveTab] = useQueryState(
    'tab',
    parseAsStringLiteral(OASIS_MANAGER_TAB_VALUES)
      .withDefault('incomplete')
      .withOptions({ history: 'replace', clearOnDefault: true })
  );
  const [isMarking, setIsMarking] = useState(false);
  // Every export failure — clinic config, CMS (iQIES) rejection, or anything
  // the server hands back — lands here so it renders on a surface that can
  // offer a next step. EMR-1225: for a CMS rejection only the server's
  // user-facing message is shown; the raw CMS detail stays in the response.
  const [exportError, setExportError] = useState<ExportError | null>(null);
  const [searchInput, setSearchInput] = useState(
    oasisManagerFilters.patientSearch
  );
  const toast = useToast();
  const navigate = useNavigate();

  // Debounce patient search — wait 350ms after typing stops before fetching
  useEffect(() => {
    const timer = setTimeout(() => {
      setOASISManagerFilters({ patientSearch: searchInput });
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const pageSize = 50;

  const fetchVisits = useCallback(async () => {
    if (activeTab === 'incomplete') return;

    setIsLoading(true);
    try {
      const status = STATUS_BY_TAB[activeTab];
      const response = await listOasisManagerVisits({
        query: {
          status: [status],
          patient_search: oasisManagerFilters.patientSearch || undefined,
          clinic_id: oasisManagerFilters.clinicId || undefined,
          hide_test: oasisManagerFilters.hideTest,
          page,
          page_size: pageSize,
        },
      });
      if (response.data) {
        setVisits(response.data.items);
        setTotal(response.data.total);
      }
    } catch (err) {
      console.error('Failed to load OASIS visits:', err);
      toast.error('Failed to load OASIS visits. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [
    activeTab,
    oasisManagerFilters.patientSearch,
    oasisManagerFilters.clinicId,
    oasisManagerFilters.hideTest,
    page,
  ]);

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  useEffect(() => {
    const visibleIds = new Set(visits.map(visit => visit.visit_id));
    setSelectedIds(prev => {
      const next = new Set(
        Array.from(prev).filter(visitId => visibleIds.has(visitId))
      );
      return next.size === prev.size ? prev : next;
    });
  }, [visits]);

  // Reset selection and page when tab changes. hideTest resets these
  // synchronously in its checkbox handler to avoid a page/fetch race.
  // The export error goes too — it describes a selection that no longer
  // exists, and its Contact Support action would report no visit IDs.
  useEffect(() => {
    setSelectedIds(new Set());
    setPage(1);
    setExportError(null);
  }, [activeTab]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === visits.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visits.map(v => v.visit_id)));
    }
  };

  const getCorrectionNum = (visitId: string): string =>
    correctionNums[visitId] ?? DEFAULT_CORRECTION_NUM;

  const setCorrectionNum = (visitId: string, value: string) => {
    setCorrectionNums(prev => ({ ...prev, [visitId]: value }));
  };

  const reportExportError = async (
    error: unknown,
    fallback: string,
    visitIds: string[]
  ): Promise<void> => {
    // Same status gate as getBusinessErrorMessage, but blob-aware (the export
    // is responseType: 'blob', so the sync helper can't read its body). A 5xx
    // body is our failure, not a business message: it can carry internal
    // detail, so it must reach neither the page nor the support request.
    const status = getHttpStatus(error);
    if (status !== undefined && status >= 500) {
      setExportError({
        title: EXPORT_FAILED_TITLE,
        message: fallback,
        detail: `HTTP ${status}`,
        visitIds,
      });
      return;
    }

    const detail = await getResponseDetailFromBlobOrJson(error);
    if (typeof detail === 'string') {
      setExportError({ title: EXPORT_FAILED_TITLE, message: detail, visitIds });
      return;
    }
    if (detail && typeof detail === 'object') {
      const parsed = detail as { message?: string; error_code?: string };
      if (parsed.error_code === 'CLINIC_CONFIG_REQUIRED' && parsed.message) {
        const message = parsed.message;
        setExportError({
          title: 'Clinic configuration required',
          message,
          visitIds,
          body: (
            <>
              {message}. Update the clinic settings in{' '}
              <button
                type='button'
                className='underline text-primary hover:text-primary/80'
                onClick={() => navigate('/admin')}
              >
                Admin &rarr; Clinics
              </button>
              . Without access to that,{' '}
              <ContactSupportLink
                subject='OASIS export failed'
                hiddenContext={{
                  error_title: 'Clinic configuration required',
                  error_detail: message,
                  visit_ids: visitIds.join(', '),
                }}
                className='text-primary'
              />
              .
            </>
          ),
        });
        return;
      }
      if (parsed.message) {
        setExportError({
          title: EXPORT_FAILED_TITLE,
          message: parsed.message,
          visitIds,
        });
        return;
      }
    }
    setExportError({
      title: EXPORT_FAILED_TITLE,
      message: getUserFacingApiMessage(error, fallback),
      visitIds,
    });
  };

  const handleExport = async () => {
    if (selectedIds.size === 0) return;
    // CMS/iQIES XML export excludes test patients (ROG-2900). Mark as Uploaded
    // still works for billing readiness — surface that instead of a silent skip.
    //
    // EMR-1770: the export builder also drops a visit type CMS cannot receive
    // (Skilled Nursing Assessment/Evaluation). Those rows stay listed on the
    // Exported tab so their Mark-as-Uploaded undo remains reachable, and Export
    // ZIP is offered on every tab, so one can be selected here. Partition it out
    // rather than counting it as exported.
    const selectedVisits = visits.filter(v => selectedIds.has(v.visit_id));
    const exportableVisits = selectedVisits.filter(
      v => !v.is_test_patient && v.is_cms_submittable !== false
    );
    const exportableIds = exportableVisits.map(v => v.visit_id);
    const skippedTestCount = selectedVisits.filter(
      v => v.is_test_patient
    ).length;
    const skippedNonSubmittableCount = selectedVisits.filter(
      v => !v.is_test_patient && v.is_cms_submittable === false
    ).length;
    if (exportableIds.length === 0) {
      toast.error(
        skippedTestCount > 0
          ? "Test patients can't be exported to CMS (iQIES). Use Mark as Uploaded for billing practice runs."
          : "CMS (iQIES) doesn't accept the selected assessment type."
      );
      return;
    }

    setIsExporting(true);
    setExportError(null);
    try {
      const visitItems = exportableIds.map(id => {
        const correctionNum = getCorrectionNum(id);
        return {
          visit_id: id,
          trans_type_cd: transTypeForCorrectionNum(correctionNum),
          correction_num: correctionNum,
        };
      });

      // EMR-1225: run the same archive past CMS (iQIES) before handing it over.
      // A rejection must not download — the user unchecks the named patients
      // and exports the rest.
      const precheck = await precheckOasisBatch({
        body: { visits: visitItems },
      });
      if (precheck.error) {
        const surfaceable = getSurfaceableError(precheck);
        if (surfaceable?.errorCode === 'oasis_vut_validation_failed') {
          setExportError({
            title: "Some assessments can't be exported",
            message: surfaceable.message,
            // CMS names exactly which assessments it rejected; support wants
            // those, not whatever is still ticked after the user reacts.
            visitIds: rejectedVisitIds(surfaceable.data) ?? exportableIds,
          });
          return;
        }
        const status = getHttpStatus(precheck);
        if (status !== undefined && status >= 400 && status < 500) {
          // A 4xx is about the request itself (unknown visit, non-OASIS type,
          // missing clinic config). The export would refuse it identically, so
          // surface it rather than downloading a ZIP we know is wrong.
          // Pass the whole result, not `.error` — the helpers below it resolve
          // the body themselves, which is what the other ~68 call sites hand in.
          await reportExportError(
            precheck,
            'Failed to validate OASIS assessments.',
            exportableIds
          );
          return;
        }
        // Anything else — 5xx, a proxy, a dropped connection — is our failure to
        // validate, not CMS's verdict. Adding a second round-trip must not make
        // exporting *less* reliable than before, so fall through and export
        // unvalidated, exactly as an iQIES outage does.
        console.error(
          'OASIS pre-check failed; exporting unvalidated',
          precheck.error
        );
      }

      // EMR-1224: always ship a ZIP, even for a single assessment, so the
      // download shape doesn't change with the number of rows selected.
      const response = await exportOasisBatch({ body: { visits: visitItems } });
      if (response.error) {
        // Whole result, not `.error` — the helpers resolve the body themselves
        // and the status gate needs the response to see a 5xx.
        await reportExportError(
          response,
          'Failed to export OASIS ZIP.',
          exportableIds
        );
        return;
      }
      if (response.data) {
        downloadBlobAsFile(
          response.data as BlobPart,
          `oasis_batch_${new Date().toISOString().slice(0, 10)}.zip`,
          'application/zip'
        );
        const exported = `Exported ${exportableIds.length} OASIS ${
          exportableIds.length === 1 ? 'assessment' : 'assessments'
        }.`;
        const skipped = [
          skippedTestCount > 0
            ? `${skippedTestCount} test patient(s) (CMS export blocked)`
            : null,
          skippedNonSubmittableCount > 0
            ? `${skippedNonSubmittableCount} assessment(s) CMS doesn't accept`
            : null,
        ].filter(Boolean);
        toast.success(
          skipped.length > 0
            ? `${exported} Skipped ${skipped.join(' and ')}.`
            : exported
        );
      }
    } catch (err) {
      // Network or unexpected errors — API errors are handled via response.error above
      console.error('Error exporting OASIS:', err);
      setExportError({
        title: EXPORT_FAILED_TITLE,
        message: 'Failed to export OASIS. Please try again.',
        // The generic copy above is not enough for support to act on.
        detail: err instanceof Error ? err.message : String(err),
        visitIds: exportableIds,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleMarkExported = async () => {
    if (selectedIds.size === 0) return;
    setIsMarking(true);
    try {
      const response = await markVisitsExported({
        body: {
          visit_ids: Array.from(selectedIds),
        },
      });
      if (response.error) {
        toast.error(
          getUserFacingApiMessage(
            response,
            'Failed to mark visits as exported.'
          )
        );
        return;
      }
      if (response.data) {
        const { updated_count, skipped_ids } = response.data;
        if (skipped_ids.length > 0) {
          toast.success(
            `Marked ${updated_count} visit(s) as exported. ${skipped_ids.length} skipped.`
          );
        } else {
          toast.success(`Marked ${updated_count} visit(s) as exported.`);
        }
        setSelectedIds(new Set());
        fetchVisits();
      }
    } catch (err) {
      console.error('Error marking visits as exported:', err);
      toast.error('Failed to mark visits as exported. Please try again.');
    } finally {
      setIsMarking(false);
    }
  };

  const handleUnmarkExported = async () => {
    if (selectedIds.size === 0) return;
    setIsMarking(true);
    try {
      const response = await unmarkVisitsExported({
        body: { visit_ids: Array.from(selectedIds) },
      });
      if (response.error) {
        toast.error('Failed to move visits back to ready.');
        return;
      }
      toast.success('Moved visit(s) back to Ready to Export.');
      setSelectedIds(new Set());
      fetchVisits();
    } catch (err) {
      console.error('Error unmarking visits:', err);
      toast.error('Failed to move visits back to ready. Please try again.');
    } finally {
      setIsMarking(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className='flex h-full flex-col'>
      <div className='border-b px-6 py-4'>
        <h1 className='text-lg font-semibold'>OASIS Manager</h1>
        <p className='text-sm text-muted-foreground'>
          Export OASIS assessments and track uploads to iQIES
        </p>
      </div>

      <div className='flex-1 overflow-auto px-6 py-4'>
        <Tabs
          value={activeTab}
          onValueChange={v => {
            const nextTab = v as OASISManagerFilters['activeTab'];
            setOASISManagerFilters({ activeTab: nextTab });
            void setActiveTab(nextTab);
          }}
        >
          <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4 mb-4'>
            <TabsList>
              <TabsTrigger value='incomplete'>Incomplete</TabsTrigger>
              <TabsTrigger value='ready_to_export'>Ready to Export</TabsTrigger>
              <TabsTrigger value='exported'>Exported</TabsTrigger>
            </TabsList>

            {activeTab !== 'incomplete' && (
              <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4 w-full md:w-auto'>
                <Input
                  placeholder='Search patients...'
                  inputSize='sm'
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className='w-full md:w-56'
                />
                <div className='flex items-center gap-2 shrink-0'>
                  <Checkbox
                    id='oasisHideTest'
                    checked={oasisManagerFilters.hideTest}
                    onCheckedChange={(checked: boolean) => {
                      setSelectedIds(new Set());
                      setPage(1);
                      setOASISManagerFilters({ hideTest: !!checked });
                    }}
                  />
                  <label
                    htmlFor='oasisHideTest'
                    className='text-sm text-muted-foreground cursor-pointer select-none whitespace-nowrap'
                  >
                    Hide test patients
                  </label>
                </div>
              </div>
            )}
          </div>

          <TabsContent value='incomplete' className='mt-0'>
            <OpenOasisReportWithQueryState showBackLink={false} />
          </TabsContent>

          {activeTab !== 'incomplete' && (
            <>
              {exportError && (
                <DismissibleExportError
                  title={exportError.title}
                  onDismiss={() => setExportError(null)}
                >
                  {exportError.body ?? (
                    <SupportMessage
                      message={exportError.message}
                      subject='OASIS export failed'
                      hiddenContext={{
                        error_title: exportError.title,
                        error_detail: exportError.detail ?? exportError.message,
                        visit_ids: exportError.visitIds.join(', '),
                      }}
                    />
                  )}
                </DismissibleExportError>
              )}

              {/* Action bar */}
              {selectedIds.size > 0 && (
                <div className='flex items-center gap-3 mb-4 p-3 bg-muted rounded-md'>
                  <span className='text-sm font-medium'>
                    {selectedIds.size} selected
                  </span>
                  <Button
                    size='sm'
                    onClick={handleExport}
                    disabled={isExporting}
                  >
                    {isExporting ? (
                      <Loader2 className='h-4 w-4 animate-spin mr-1' />
                    ) : (
                      <Download className='h-4 w-4 mr-1' />
                    )}
                    Export ZIP
                  </Button>
                  {activeTab === 'ready_to_export' && (
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={handleMarkExported}
                      disabled={isMarking}
                    >
                      {isMarking ? (
                        <Loader2 className='h-4 w-4 animate-spin mr-1' />
                      ) : (
                        <Upload className='h-4 w-4 mr-1' />
                      )}
                      Mark as Uploaded
                    </Button>
                  )}
                  {activeTab === 'exported' && (
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={handleUnmarkExported}
                      disabled={isMarking}
                    >
                      {isMarking ? (
                        <Loader2 className='h-4 w-4 animate-spin mr-1' />
                      ) : (
                        <Undo2 className='h-4 w-4 mr-1' />
                      )}
                      Move to Ready
                    </Button>
                  )}
                </div>
              )}

              <TabsContent value={activeTab} className='mt-0'>
                {isLoading ? (
                  <div className='flex items-center justify-center py-12'>
                    <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
                  </div>
                ) : visits.length === 0 ? (
                  <div className='flex items-center justify-center py-12 text-sm text-muted-foreground'>
                    {activeTab === 'ready_to_export'
                      ? 'No completed OASIS visits ready to export.'
                      : 'No exported OASIS visits.'}
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className='w-10'>
                            <Checkbox
                              aria-label='Select all visits'
                              checked={
                                visits.length > 0 &&
                                selectedIds.size === visits.length
                              }
                              onCheckedChange={toggleSelectAll}
                            />
                          </TableHead>
                          <TableHead>Patient</TableHead>
                          <TableHead>Visit Type</TableHead>
                          <TableHead>Visit Date</TableHead>
                          {activeTab === 'ready_to_export' && (
                            <TableHead>Days Remaining</TableHead>
                          )}
                          <TableHead>
                            <div className='flex items-center gap-1'>
                              Correction #
                              <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type='button'
                                      className='inline-flex text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm'
                                      aria-label='About correction numbers'
                                    >
                                      <HelpCircle className='h-3.5 w-3.5' />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side='bottom'
                                    className='max-w-xs text-xs'
                                  >
                                    <p>
                                      Only change the correction number if you
                                      are retransmitting an already accepted
                                      OASIS and need to resubmit a corrected
                                      one. For the first resubmission use 01,
                                      second 02, and so on.
                                    </p>
                                    <p className='mt-1.5'>
                                      If an OASIS was rejected and you are
                                      retransmitting after making corrections,
                                      leave the correction number as 00.
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </TableHead>
                          <TableHead>Clinician</TableHead>
                          <TableHead>Clinic</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {visits.map(visit => (
                          <TableRow key={visit.visit_id}>
                            <TableCell>
                              <Checkbox
                                aria-label={`Select ${visit.patient_name}`}
                                checked={selectedIds.has(visit.visit_id)}
                                onCheckedChange={() =>
                                  toggleSelect(visit.visit_id)
                                }
                              />
                            </TableCell>
                            <TableCell className='font-medium'>
                              <div className='flex items-center gap-2'>
                                <ReportLink
                                  to={`/patients/${visit.patient_id}`}
                                >
                                  {visit.patient_name}
                                </ReportLink>
                                {visit.is_test_patient && (
                                  <TestPatientBadge className='shrink-0' />
                                )}
                                {visit.show_imported_badge && (
                                  <ImportedPatientBadge
                                    importedAt={visit.imported_at}
                                    className='shrink-0'
                                  />
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <ReportLink
                                to={`/patients/${visit.patient_id}/visits/${visit.visit_id}`}
                              >
                                <Badge
                                  variant='outline'
                                  className='text-xs whitespace-nowrap'
                                >
                                  {getVisitTypeDisplayName(visit.visit_type)}
                                </Badge>
                              </ReportLink>
                            </TableCell>
                            <TableCell>
                              {formatDateToMMDDYYYY(visit.visit_date)}
                            </TableCell>
                            {activeTab === 'ready_to_export' && (
                              <TableCell>
                                {(() => {
                                  const days = daysUntilDeadline(
                                    visit.visit_date
                                  );
                                  return (
                                    <span
                                      className={
                                        days <= 0
                                          ? 'text-destructive font-medium'
                                          : days <= 7
                                            ? 'text-orange-500 font-medium'
                                            : 'text-muted-foreground'
                                      }
                                    >
                                      {days < 0
                                        ? 'Overdue'
                                        : days === 0
                                          ? 'Due today'
                                          : `${days}d`}
                                    </span>
                                  );
                                })()}
                              </TableCell>
                            )}
                            <TableCell>
                              <Select
                                value={getCorrectionNum(visit.visit_id)}
                                onValueChange={value =>
                                  setCorrectionNum(visit.visit_id, value)
                                }
                              >
                                <SelectTrigger
                                  selectSize='sm'
                                  className='w-20'
                                  aria-label='Correction number'
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {CORRECTION_NUM_OPTIONS.map(num => (
                                    <SelectItem key={num} value={num}>
                                      {num}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              {visit.assigned_clinician_name ?? '—'}
                            </TableCell>
                            <TableCell>{visit.clinic_name ?? '—'}</TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  visit.status === 'exported'
                                    ? 'default'
                                    : 'secondary'
                                }
                                className='text-xs'
                              >
                                {visit.status === 'exported'
                                  ? 'Exported'
                                  : 'Completed'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className='flex items-center justify-between mt-4'>
                        <span className='text-sm text-muted-foreground'>
                          Showing {(page - 1) * pageSize + 1}–
                          {Math.min(page * pageSize, total)} of {total}
                        </span>
                        <div className='flex gap-2'>
                          <Button
                            size='sm'
                            variant='outline'
                            disabled={page <= 1}
                            onClick={() => setPage(p => p - 1)}
                          >
                            Previous
                          </Button>
                          <Button
                            size='sm'
                            variant='outline'
                            disabled={page >= totalPages}
                            onClick={() => setPage(p => p + 1)}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default OASISManager;
